import type { EditorView } from 'prosemirror-view'
import { Fragment, Slice, type Node as PMNode } from 'prosemirror-model'
import type { EditorContext } from './tiptap-editor-context'
import { NOTCH_ELEMENTS_CLIPBOARD_MIME } from './notch-elements-clipboard'
import { enqueueSlashCommandBlockedSnackbar } from './tiptap-slash-blocked-snackbar'

/** The fragment without iframe or bookmark nodes at any depth; `removed` says whether it dropped any. */
function withoutIframes(fragment: Fragment): { fragment: Fragment; removed: boolean } {
  const kept: PMNode[] = []
  let removed = false
  fragment.forEach(node => {
    if (node.type.name === 'iframe' || node.type.name === 'bookmark') {
      removed = true
      return
    }
    const inner = withoutIframes(node.content)
    removed = removed || inner.removed
    // A wrapper emptied by the removal would be invalid content: drop it with the embed it held
    if (inner.removed && inner.fragment.childCount === 0) return
    kept.push(inner.removed ? node.copy(inner.fragment) : node)
  })
  return { fragment: removed ? Fragment.fromArray(kept) : fragment, removed }
}

/** Blocks canvas element clipboard payloads and embeds inside content-scoped block editors. */
export function handleContentScopedPaste(
  ctx: Pick<EditorContext, 'slashScope'>,
  view: EditorView,
  event: ClipboardEvent,
  slice: Slice
): boolean {
  if (ctx.slashScope !== 'content') {
    return false
  }
  if (event.clipboardData?.types.includes(NOTCH_ELEMENTS_CLIPBOARD_MIME)) {
    event.preventDefault()
    enqueueSlashCommandBlockedSnackbar('scope')
    return true
  }
  // An embed or bookmark is an element of its own: pasting one would nest it inside the block
  const stripped = withoutIframes(slice.content)
  if (!stripped.removed) {
    return false
  }
  event.preventDefault()
  enqueueSlashCommandBlockedSnackbar('scope')
  // Nothing but embeds was pasted: leave the selection alone
  if (stripped.fragment.childCount === 0) {
    return true
  }
  // The removal can shorten the open edges, so clamp to what the remaining fragment can open
  const max = Slice.maxOpen(stripped.fragment)
  const kept = new Slice(
    stripped.fragment,
    Math.min(slice.openStart, max.openStart),
    Math.min(slice.openEnd, max.openEnd)
  )
  view.dispatch(
    view.state.tr.replaceSelection(kept).scrollIntoView().setMeta('paste', true).setMeta('uiEvent', 'paste')
  )
  return true
}

/** Blocks dropping canvas elements and embeds into content-scoped block editors. */
export function handleContentScopedDrop(
  ctx: Pick<EditorContext, 'slashScope'>,
  _view: EditorView,
  event: DragEvent,
  slice: Slice,
  _moved: boolean
): boolean {
  if (ctx.slashScope !== 'content') {
    return false
  }
  // An embed or bookmark dragged in from a table cell is an element of its own, as with paste
  if (!event.dataTransfer?.types.includes(NOTCH_ELEMENTS_CLIPBOARD_MIME) && !withoutIframes(slice.content).removed) {
    return false
  }
  event.preventDefault()
  enqueueSlashCommandBlockedSnackbar('scope')
  return true
}
